import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph } from '@digdir/designsystemet-react';
import type { AssessedArea, DimensionSummary } from '../../../api/workEnvironmentTypes';
import type { FactorDimension } from '../../../api/hms';
import {
  BALANCE_RANGE,
  balancePosition,
  barPercent,
  dimensionBalance,
  dimensionColumns,
  inNumbersIntro,
  oneDecimal,
  signedDecimal,
} from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  areas: AssessedArea[];
  dimensions: Record<FactorDimension, DimensionSummary | null>;
};

const TEXTS: Record<FactorDimension, { title: string; help: string; stat: string }> = {
  LOAD: {
    title: 'workEnvironmentRisk.inNumbers.loadsTitle',
    help: 'workEnvironmentRisk.inNumbers.loadsHelp',
    stat: 'workEnvironmentRisk.inNumbers.loads',
  },
  RESOURCE: {
    title: 'workEnvironmentRisk.inNumbers.resourcesTitle',
    help: 'workEnvironmentRisk.inNumbers.resourcesHelp',
    stat: 'workEnvironmentRisk.inNumbers.resources',
  },
};

const DimensionStat = ({ kind, label, value }: { kind: string; label: string; value: string }) => (
  <p className={classes.dimensionStat} data-kind={kind}>
    <span className="ds-paragraph" data-size="sm">
      {label}
    </span>
    <span className="ds-heading" data-size="lg">
      {value}
    </span>
  </p>
);

/** Strains and resources as means, each on its own, as the Job Demands–Resources model measures them */
export const InNumbersCard = ({ areas, dimensions }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const columns = dimensionColumns(dimensions).map((column) => ({ ...column, ...TEXTS[column.dimension] }));
  const intro = inNumbersIntro(areas, dimensions, t);
  const balance = dimensionBalance(dimensions);
  // By dimension, not by place: a team with only resources has no strain column
  const load = columns.find((column) => column.dimension === 'LOAD');
  const resource = columns.find((column) => column.dimension === 'RESOURCE');

  return (
    <section aria-labelledby="in-numbers-heading" className={process.card}>
      <div className={process.cardBody}>
        <Heading level={2} data-size="sm" id="in-numbers-heading">
          {t('workEnvironmentRisk.inNumbers.title')}
        </Heading>
        {intro && <Paragraph className={classes.intro}>{intro}</Paragraph>}
        <div className={classes.dimensionStats}>
          {load && (
            <DimensionStat
              kind="load"
              label={t(load.stat)}
              value={t('workEnvironmentRisk.inNumbers.outOf', {
                value: oneDecimal(load.summary.average, i18n.language),
              })}
            />
          )}
          {balance !== null && (
            <DimensionStat
              kind="balance"
              label={t('workEnvironmentRisk.inNumbers.balance')}
              value={signedDecimal(balance, i18n.language)}
            />
          )}
          {resource && (
            <DimensionStat
              kind="resource"
              label={t(resource.stat)}
              value={t('workEnvironmentRisk.inNumbers.outOf', {
                value: oneDecimal(resource.summary.average, i18n.language),
              })}
            />
          )}
        </div>
        {balance !== null && (
          <div className={classes.balance}>
            <span
              role="img"
              aria-label={t('workEnvironmentRisk.inNumbers.balanceLabel', {
                value: signedDecimal(balance, i18n.language),
              })}
              className={classes.balanceStripe}
            >
              <span className={classes.balanceMarker} style={{ left: balancePosition(balance) }} />
            </span>
            <span aria-hidden="true" className={`ds-paragraph ${classes.balanceScale}`} data-size="xs">
              <span>{t('workEnvironmentRisk.inNumbers.loadsWeigh', { value: `−${BALANCE_RANGE}` })}</span>
              <span>{t('workEnvironmentRisk.inNumbers.neutral')}</span>
              <span>
                {t('workEnvironmentRisk.inNumbers.resourcesWeigh', {
                  value: `+${BALANCE_RANGE}`,
                })}
              </span>
            </span>
          </div>
        )}
        <Paragraph data-size="sm" className={process.subtle}>
          {t('workEnvironmentRisk.inNumbers.note')}
        </Paragraph>
      </div>
      <div className={classes.dimensionColumns}>
        {columns.map(({ dimension, title, help, summary }) => (
          <div key={dimension} className={classes.dimensionColumn}>
            <Heading level={3} data-size="2xs">
              {t(title)}
            </Heading>
            <Paragraph data-size="xs" className={process.subtle}>
              {t(help)}
            </Paragraph>
            <ul className={classes.dimensionList}>
              {summary.areas.map((area) => (
                <li key={area.key} className={classes.dimensionRow}>
                  <span className="ds-paragraph" data-size="sm">
                    {area.name}
                  </span>
                  <span className="ds-label" data-size="sm">
                    {oneDecimal(area.score, i18n.language)}
                  </span>
                  <span aria-hidden="true" className={classes.bar} data-dimension={dimension}>
                    <span style={{ width: barPercent(area.score) }} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};
